import { ChevronRight, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Interruptor } from "../../components/ui/primitivos";
import {
  actualizarUsuario,
  asignarRoles,
  cambiarEstadoUsuario,
  fetchRolesAsignables,
  fetchUsuario,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { fechaHora } from "../produccion/orden";
import { rolesParaAgregar, sePuedeQuitar } from "./usuario";

/**
 * El detalle de una persona, porteado de `UserDetailPage.tsx` (ADR 0067).
 *
 * **Es una pantalla y no un modal** (ADR 0064), con las dos tarjetas de allá:
 * **Información básica** —email en solo lectura, nombre, estado— y **Roles
 * asignados** —insignias con su cruz para quitar, y un selector para sumar—.
 *
 * El modal tenía las dos cosas apretadas en una columna, los roles como una
 * lista de casillas, y **no mostraba el estado ni el último ingreso**: para
 * activar o desactivar había que volver al listado.
 */
export function UsuarioPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { me } = useSession();

  const detalle = useDatos(() => fetchUsuario(id), id);
  const asignables = useDatos(fetchRolesAsignables);

  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function accion(fn: () => Promise<unknown>) {
    setTrabajando(true);
    setError(null);
    try {
      await fn();
      await detalle.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    } finally {
      setTrabajando(false);
    }
  }

  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Usuario" alVolver={() => navegar("/usuarios")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }

  const datos = detalle.datos;
  if (!datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando usuario…</p>;
  }

  const { usuario, gestionable } = datos;
  const esUnoMismo = usuario.id === me?.id;

  return (
    <div className="space-y-6">
      <nav aria-label="Miga de pan" className="flex items-center text-neutral-500 text-sm">
        <Link to="/usuarios" className="hover:text-neutral-900">
          Usuarios
        </Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="font-medium text-neutral-900">{usuario.nombre}</span>
      </nav>

      <PageHeader
        titulo={usuario.nombre}
        subtitulo={usuario.email}
        alVolver={() => navegar("/usuarios")}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Etiqueta tono={usuario.activo ? "ok" : "error"}>
            {usuario.activo ? "Activo" : "Inactivo"}
          </Etiqueta>
          {esUnoMismo ? <Etiqueta>Sos vos</Etiqueta> : null}
          <span className="ml-auto text-neutral-500 text-xs">
            Último ingreso: {fechaHora(usuario.ultimoLogin)}
          </span>
        </div>
      </PageHeader>

      {error ? <Aviso>{error}</Aviso> : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <DatosBasicos
          usuario={usuario}
          gestionable={gestionable}
          esUnoMismo={esUnoMismo}
          trabajando={trabajando}
          accion={accion}
        />
        <RolesAsignados
          usuario={usuario}
          asignables={asignables.datos ?? []}
          gestionable={gestionable}
          esUnoMismo={esUnoMismo}
          trabajando={trabajando}
          accion={accion}
        />
      </div>
    </div>
  );
}

type Usuario = Awaited<ReturnType<typeof fetchUsuario>>["usuario"];
type Accion = (fn: () => Promise<unknown>) => Promise<void>;

/**
 * Los datos de contacto y el estado.
 *
 * **El email no se edita**: es la credencial de acceso y su cambio es un flujo
 * de auth, no de consola. Va en solo lectura, como en `imprenta`.
 */
function DatosBasicos({
  usuario,
  gestionable,
  esUnoMismo,
  trabajando,
  accion,
}: {
  usuario: Usuario;
  gestionable: boolean;
  esUnoMismo: boolean;
  trabajando: boolean;
  accion: Accion;
}) {
  const [nombre, setNombre] = useState(usuario.nombre);
  const [telefono, setTelefono] = useState(usuario.telefono ?? "");
  const [direccion, setDireccion] = useState(usuario.direccion ?? "");

  // Uno SÍ puede editarse sus datos de contacto: cambiarse el teléfono no es
  // un riesgo. Lo que no puede es cambiarse el estado ni los roles (ADR 0019).
  const puedeEditarDatos = gestionable || esUnoMismo;

  return (
    <Seccion titulo="Información básica" descripcion="Datos generales de la persona">
      <Campo etiqueta="Email" value={usuario.email} disabled readOnly />
      <Campo
        etiqueta="Nombre"
        value={nombre}
        disabled={!puedeEditarDatos}
        onChange={(e) => setNombre(e.target.value)}
      />
      <Campo
        etiqueta="Teléfono"
        value={telefono}
        disabled={!puedeEditarDatos}
        onChange={(e) => setTelefono(e.target.value)}
      />
      <Campo
        etiqueta="Dirección"
        value={direccion}
        disabled={!puedeEditarDatos}
        onChange={(e) => setDireccion(e.target.value)}
      />

      <div className="flex items-center justify-between border-neutral-200 border-t pt-3">
        <div>
          <p className="font-medium text-neutral-900 text-sm">Estado</p>
          <p className="text-neutral-500 text-sm">
            {usuario.activo ? "Puede entrar al sistema" : "No puede entrar al sistema"}
          </p>
        </div>
        {/* Nadie se desactiva a sí mismo (ADR 0019): el servidor lo rechaza, y
            ofrecerlo sería prometer algo que no pasa. */}
        <Interruptor
          aria-label="Activo"
          checked={usuario.activo}
          disabled={!gestionable || trabajando}
          onCheckedChange={(activo) => accion(() => cambiarEstadoUsuario(usuario.id, activo))}
        />
      </div>

      {puedeEditarDatos ? (
        <Boton
          className="w-full"
          disabled={trabajando || nombre.trim().length === 0}
          onClick={() =>
            accion(() =>
              actualizarUsuario(usuario.id, {
                nombre,
                telefono: telefono.trim() || null,
                direccion: direccion.trim() || null,
              }),
            )
          }
        >
          {trabajando ? "Guardando..." : "Guardar cambios"}
        </Boton>
      ) : null}
    </Seccion>
  );
}

/**
 * Los roles, como insignias con su cruz — el patrón de `UserDetailPage`.
 *
 * El selector ofrece solo los que el worker va a aceptar (`/roles-asignables`,
 * filtrado por contención). Es conveniencia, no control: el rechazo del
 * servidor sigue estando igual (ADR 0019).
 */
function RolesAsignados({
  usuario,
  asignables,
  gestionable,
  esUnoMismo,
  trabajando,
  accion,
}: {
  usuario: Usuario;
  asignables: readonly string[];
  gestionable: boolean;
  esUnoMismo: boolean;
  trabajando: boolean;
  accion: Accion;
}) {
  const [aAgregar, setAAgregar] = useState("");
  const disponibles = rolesParaAgregar(asignables, usuario.roles);

  const cambiar = (roles: string[]) => accion(() => asignarRoles(usuario.id, roles));

  return (
    <Seccion titulo="Roles asignados" descripcion="Qué puede hacer esta persona">
      <div className="flex flex-wrap gap-2">
        {usuario.roles.length === 0 ? (
          <span className="text-neutral-500 text-sm">Sin roles asignados</span>
        ) : (
          usuario.roles.map((rol) => (
            <span
              key={rol}
              className="inline-flex items-center gap-1 rounded bg-neutral-200 px-2 py-0.5 font-medium text-neutral-700 text-xs"
            >
              {rol}
              {sePuedeQuitar(usuario.roles, gestionable) ? (
                <button
                  type="button"
                  aria-label={`Quitar ${rol}`}
                  disabled={trabajando}
                  onClick={() => cambiar(usuario.roles.filter((r) => r !== rol))}
                  className="hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </span>
          ))
        )}
      </div>

      {esUnoMismo ? (
        // Nadie se cambia sus propios roles (ADR 0019): camino corto tanto a la
        // escalada como al lockout.
        <p className="border-neutral-200 border-t pt-3 text-neutral-500 text-sm">
          No podés cambiarte tus propios roles.
        </p>
      ) : gestionable && disponibles.length > 0 ? (
        <div className="space-y-1 border-neutral-200 border-t pt-3">
          <label htmlFor="agregar-rol" className="block text-neutral-600 text-sm">
            Agregar rol
          </label>
          <div className="flex gap-2">
            <select
              id="agregar-rol"
              value={aAgregar}
              onChange={(e) => setAAgregar(e.target.value)}
              className="flex-1 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
            >
              <option value="">— elegir —</option>
              {disponibles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <Boton
              disabled={trabajando || aAgregar === ""}
              onClick={async () => {
                await cambiar([...usuario.roles, aAgregar]);
                setAAgregar("");
              }}
            >
              Agregar
            </Boton>
          </div>
        </div>
      ) : null}
    </Seccion>
  );
}

function Seccion({
  titulo,
  descripcion,
  children,
}: {
  titulo: string;
  descripcion: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="font-semibold text-neutral-900 text-sm">{titulo}</h2>
        <p className="text-neutral-500 text-sm">{descripcion}</p>
      </div>
      {children}
    </Card>
  );
}
